import { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, MessageSquare, X, Upload, Trash2, GitBranch, History, User, ChevronDown, Check, Plus, EllipsisVertical, CircleUserRound, Save, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addTaskAttachment,
  addTaskComment,
  addTaskRelation,
  deleteTask,
  deleteTaskAttachment,
  deleteTaskRelation,
  fetchTaskBoard,
  fetchTaskBoardDetail,
  fetchTaskCreateLookups,
  searchTasks as searchTasksApi,
  updateTask,
  type TaskCreateLookups,
  type TaskBoardDetail,
  type TaskBoardItem,
  type TaskBoardRelatedTask,
} from '@/api/tasks';
import { useI18n } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import ImageCarouselModal from './ImageCarouselModal';
import TaskCreateModal from './TaskCreateModal';
import type { TaskCreateRouteState } from './TaskCreatePage';
import { confirmToast, useErrorToast, LoadRetry } from '@/lib/notify';

type BoardStatus = 1 | 2 | 3 | 4 | 5;
type BoardTouchDrag = { task: TaskBoardItem; moved: boolean };
type BoardFilter = 'all' | 'mine';

const TASK_CATEGORY_COLORS: Record<string, { border: string; background: string; text: string; dot: string }> = {
  bakim: {
    border: '#0f766e',
    background: 'rgba(15, 118, 110, 0.14)',
    text: '#0f766e',
    dot: '#0f766e',
  },
  ariza: {
    border: '#dc2626',
    background: 'rgba(220, 38, 38, 0.12)',
    text: '#b91c1c',
    dot: '#dc2626',
  },
  kontrol: {
    border: '#2563eb',
    background: 'rgba(37, 99, 235, 0.12)',
    text: '#1d4ed8',
    dot: '#2563eb',
  },
  temizlik: {
    border: '#7c3aed',
    background: 'rgba(124, 58, 237, 0.12)',
    text: '#6d28d9',
    dot: '#7c3aed',
  },
  denetim: {
    border: '#d97706',
    background: 'rgba(217, 119, 6, 0.14)',
    text: '#b45309',
    dot: '#d97706',
  },
  montaj: {
    border: '#0891b2',
    background: 'rgba(8, 145, 178, 0.12)',
    text: '#0e7490',
    dot: '#0891b2',
  },
  inceleme: {
    border: '#4f46e5',
    background: 'rgba(79, 70, 229, 0.12)',
    text: '#4338ca',
    dot: '#4f46e5',
  },
  arastirma: {
    border: '#7c3aed',
    background: 'rgba(124, 58, 237, 0.12)',
    text: '#6d28d9',
    dot: '#7c3aed',
  },
  planlama: {
    border: '#2563eb',
    background: 'rgba(37, 99, 235, 0.12)',
    text: '#1d4ed8',
    dot: '#2563eb',
  },
  devreyealma: {
    border: '#059669',
    background: 'rgba(5, 150, 105, 0.12)',
    text: '#047857',
    dot: '#059669',
  },
  test: {
    border: '#ca8a04',
    background: 'rgba(202, 138, 4, 0.14)',
    text: '#a16207',
    dot: '#ca8a04',
  },
  kalite: {
    border: '#9333ea',
    background: 'rgba(147, 51, 234, 0.12)',
    text: '#7e22ce',
    dot: '#9333ea',
  },
  takip: {
    border: '#ea580c',
    background: 'rgba(234, 88, 12, 0.12)',
    text: '#c2410c',
    dot: '#ea580c',
  },
  destek: {
    border: '#0284c7',
    background: 'rgba(2, 132, 199, 0.12)',
    text: '#0369a1',
    dot: '#0284c7',
  },
  revizyon: {
    border: '#be123c',
    background: 'rgba(190, 18, 60, 0.12)',
    text: '#9f1239',
    dot: '#be123c',
  },
};

const TASK_CATEGORY_COLOR_FALLBACKS = [
  { border: '#0f766e', background: 'rgba(15, 118, 110, 0.14)', text: '#0f766e', dot: '#0f766e' },
  { border: '#2563eb', background: 'rgba(37, 99, 235, 0.12)', text: '#1d4ed8', dot: '#2563eb' },
  { border: '#7c3aed', background: 'rgba(124, 58, 237, 0.12)', text: '#6d28d9', dot: '#7c3aed' },
  { border: '#d97706', background: 'rgba(217, 119, 6, 0.14)', text: '#b45309', dot: '#d97706' },
  { border: '#059669', background: 'rgba(5, 150, 105, 0.12)', text: '#047857', dot: '#059669' },
  { border: '#dc2626', background: 'rgba(220, 38, 38, 0.12)', text: '#b91c1c', dot: '#dc2626' },
  { border: '#0891b2', background: 'rgba(8, 145, 178, 0.12)', text: '#0e7490', dot: '#0891b2' },
  { border: '#9333ea', background: 'rgba(147, 51, 234, 0.12)', text: '#7e22ce', dot: '#9333ea' },
];

function normalizeTaskCategoryKey(value: string | null | undefined) {
  return (value ?? '')
    .toLocaleLowerCase('tr-TR')
    .replaceAll('ğ', 'g')
    .replaceAll('ü', 'u')
    .replaceAll('ş', 's')
    .replaceAll('ı', 'i')
    .replaceAll('ö', 'o')
    .replaceAll('ç', 'c')
    .replace(/[^a-z0-9]+/g, '');
}

function getTaskCategoryColors(categoryName: string | null | undefined) {
  const key = normalizeTaskCategoryKey(categoryName);
  if (TASK_CATEGORY_COLORS[key]) return TASK_CATEGORY_COLORS[key];

  const hash = key.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return TASK_CATEGORY_COLOR_FALLBACKS[hash % TASK_CATEGORY_COLOR_FALLBACKS.length];
}

export function TaskCategoryFilterDropdown({
  value,
  onChange,
  options,
  label,
  compact = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: TaskCreateLookups['taskcategories'];
  label: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selected = options.find((option) => String(option.id) === value) ?? null;
  const selectedColors = getTaskCategoryColors(selected?.description);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex ${compact?'h-8 min-w-[150px] rounded-lg':'h-10 min-w-[190px] rounded-xl'} items-center justify-between gap-3 border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600`}
      >
        <span className="inline-flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: selected ? selectedColors.dot : '#cbd5e1' }} />
          <span>{selected?.description ?? label}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <button
            type="button"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
            className="flex w-full items-center justify-between px-3 py-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              {label}
            </span>
            {!value && <Check className="h-4 w-4 text-slate-500" />}
          </button>
          {options.map((option) => {
            const colors = getTaskCategoryColors(option.description);
            const active = String(option.id) === value;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(String(option.id));
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between px-3 py-2.5 text-xs font-semibold transition hover:bg-slate-50"
                style={{ color: colors.text }}
              >
                <span className="inline-flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors.dot }} />
                  {option.description}
                </span>
                {active && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function statusTitle(status: BoardStatus, t: (key: string) => string): string {
  if (status === 1) return t('tasks.status.todo');
  if (status === 2) return t('tasks.status.inProgress');
  if (status === 3) return t('tasks.status.done');
  if (status === 4) return t('tasks.status.canceled');
  return t('tasks.status.closed');
}

function statusTone(status: BoardStatus): string {
  if (status === 2) return 'bg-blue-100 text-blue-700';
  if (status === 3) return 'bg-emerald-100 text-emerald-700';
  if (status === 4) return 'bg-rose-100 text-rose-700';
  if (status === 5) return 'bg-slate-200 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

function priorityTone(priority: number): string {
  if (priority === 3) return 'bg-rose-100 text-rose-700';
  if (priority === 2) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
}

function isImageAttachment(path: string, mimeType: string | null): boolean {
  if (mimeType?.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(path);
}


function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('tr-TR');
}

function getDueDateInfo(value: string | null | undefined) {
  if (!value) return null;
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays === 0) return { label: 'Bugun', className: 'text-amber-700 bg-amber-100' };
  if (diffDays > 0) return { label: `${diffDays} gun kaldi`, className: 'text-emerald-700 bg-emerald-100' };
  return { label: `${Math.abs(diffDays)} gun gecti`, className: 'text-rose-700 bg-rose-100' };
}

const BOARD_COLUMNS: { status: BoardStatus; key: string }[] = [
  { status: 1, key: 'todo' },
  { status: 2, key: 'in_progress' },
  { status: 3, key: 'done' },
  { status: 5, key: 'closed' },
  { status: 4, key: 'canceled' },
];

export function TaskBoardContent({
  embedded = false,
  filter = 'all',
  search = '',
  categoryFilter: controlledCategoryFilter,
  onCategoryFilterChange,
  categoryOptions,
}: {
  embedded?: boolean;
  filter?: BoardFilter;
  search?: string;
  categoryFilter?: string;
  onCategoryFilterChange?: (value: string) => void;
  categoryOptions?: TaskCreateLookups['taskcategories'];
}) {
  const { t } = useI18n();
  const currentUser = useAuthStore((s) => s.user);
  const [tasks, setTasks] = useState<TaskBoardItem[]>([]);
  const [lookups, setLookups] = useState<TaskCreateLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createState, setCreateState] = useState<TaskCreateRouteState | null>(null);
  const [internalCategoryFilter, setInternalCategoryFilter] = useState('');
  const [touchTargetStatus, setTouchTargetStatus] = useState<BoardStatus | null>(null);
  const touchDragRef = useRef<BoardTouchDrag | null>(null);
  const suppressClickRef = useRef(false);
  const categoryFilter = controlledCategoryFilter ?? internalCategoryFilter;
  const setCategoryFilter = onCategoryFilterChange ?? setInternalCategoryFilter;

  async function loadBoard() {
    setLoading(true);
    setError(null);
    try {
      const [board, lookupData] = await Promise.all([
        fetchTaskBoard(),
        fetchTaskCreateLookups().catch(() => null),
      ]);
      setTasks(board);
          setLookups(lookupData);
    } catch {
      setError(t('tasks.loadError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadBoard();
  }, [t]);

  const grouped = useMemo(() => {
    const groups: Record<BoardStatus, TaskBoardItem[]> = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    const searchTerms = search.trim().toLocaleUpperCase('tr-TR').split(/\s+/).filter(Boolean);
    const visibleTasks = tasks.filter((task) => {
      if (filter === 'mine' && currentUser && !(task.assignees ?? []).some((assignee) => assignee.id === currentUser.id)) {
        return false;
      }
      if (categoryFilter && String(task.task_category_id) !== categoryFilter) {
        return false;
      }
      if (searchTerms.length) {
        const text = [
          task.id, task.title, task.description, task.power_plant_name, task.task_category_name,
          task.task_created_date, task.task_due_date,
          statusTitle(task.task_status as BoardStatus, t),
          task.priority === 1 ? t('tasks.priorityLabel.low')
            : task.priority === 2 ? t('tasks.priorityLabel.middle')
              : task.priority === 3 ? t('tasks.priorityLabel.high') : task.priority,
          ...(task.assignees ?? []).map(assignee => assignee.fullname),
        ].join(' ').toLocaleUpperCase('tr-TR');
        if (!searchTerms.every(term => text.includes(term))) return false;
      }
      return true;
    });
    for (const task of visibleTasks) {
      const status = (task.task_status as BoardStatus) ?? 1;
      (groups[status] ?? groups[1]).push(task);
    }
    return groups;
  }, [categoryFilter, currentUser, filter, search, t, tasks]);

  const taskByUuid = useMemo(() => {
    const map = new Map<string, TaskBoardItem>();
    for (const task of tasks) map.set(task.uuid_id, task);
    return map;
  }, [tasks]);

  async function moveTask(task: TaskBoardItem, nextStatus: BoardStatus) {
    if ((task.task_status as BoardStatus) === nextStatus) return;

    setTasks((current) =>
      current.map((item) => (item.uuid_id === task.uuid_id ? { ...item, task_status: nextStatus } : item)),
    );

    try {
      await updateTask(task.uuid_id, { taskStatus: nextStatus });
    } catch {
      toast.error(t('tasks.updateError'));
      await loadBoard();
    }
  }

  function statusFromPoint(clientX: number, clientY: number): BoardStatus | null {
    const target = document.elementFromPoint(clientX, clientY)?.closest('[data-board-status]');
    const value = target?.getAttribute('data-board-status');
    if (value === '1' || value === '2' || value === '3' || value === '4' || value === '5') return Number(value) as BoardStatus;
    return null;
  }

  function handleTouchDragStart(task: TaskBoardItem) {
    touchDragRef.current = { task, moved: false };
    setTouchTargetStatus((task.task_status as BoardStatus) ?? 1);
  }

  function handleTouchDragMove(clientX: number, clientY: number) {
    if (!touchDragRef.current) return;
    touchDragRef.current.moved = true;
    setTouchTargetStatus(statusFromPoint(clientX, clientY));
  }

  function handleTouchDragEnd(clientX: number, clientY: number) {
    const activeDrag = touchDragRef.current;
    const nextStatus = statusFromPoint(clientX, clientY) ?? touchTargetStatus;
    touchDragRef.current = null;
    setTouchTargetStatus(null);
    if (!activeDrag?.moved || !nextStatus) return;
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 250);
    void moveTask(activeDrag.task, nextStatus);
  }

  function shouldSuppressClick() {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }

  if (error) return <LoadRetry />;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">{t('tasks.boardTitle')}</h1>
            {!loading && <p className="text-xs text-gray-400">{t('tasks.count', { count: tasks.length })}</p>}
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('tasks.new')}
          </button>
        </div>
      )}

      {!embedded && (
        <div className="flex items-center justify-end gap-3">
          <TaskCategoryFilterDropdown
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryOptions ?? lookups?.taskcategories ?? []}
            label={t('tasks.filters.allCategories')}
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
        <div className="grid h-full min-w-[1220px] grid-cols-5 gap-4">
          {BOARD_COLUMNS.map((col) => (
            <BoardColumn
              key={col.key}
              title={statusTitle(col.status, t)}
              status={col.status}
              tasks={grouped[col.status]}
              findTask={(uuid) => taskByUuid.get(uuid) ?? null}
              loading={loading}
              onOpen={(uuid) => setActiveUuid(uuid)}
              onMove={moveTask}
              touchTargetStatus={touchTargetStatus}
              onTouchDragStart={handleTouchDragStart}
              onTouchDragMove={handleTouchDragMove}
              onTouchDragEnd={handleTouchDragEnd}
              shouldSuppressClick={shouldSuppressClick}
            />
          ))}
        </div>
      </div>

      {activeUuid && (
        <TaskBoardDrawer
          uuid={activeUuid}
          onClose={() => setActiveUuid(null)}
          onRefresh={loadBoard}
          onOpenCreate={(state) => {
            setActiveUuid(null);
            setCreateState(state);
            setCreateOpen(true);
          }}
        />
      )}

      <TaskCreateModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateState(null);
        }}
        onCreated={loadBoard}
        initialValues={createState?.initialValues}
        mode={createState?.mode}
        sourceTaskUuidId={createState?.sourceTaskUuidId}
        sourceTaskLabel={createState?.sourceTaskLabel}
      />
    </div>
  );
}

export default function TaskBoardPage() {
  return <TaskBoardContent />;
}

function BoardColumn({
  title,
  status,
  tasks,
  findTask,
  loading,
  onOpen,
  onMove,
  touchTargetStatus,
  onTouchDragStart,
  onTouchDragMove,
  onTouchDragEnd,
  shouldSuppressClick,
}: {
  title: string;
  status: BoardStatus;
  tasks: TaskBoardItem[];
  findTask: (uuid: string) => TaskBoardItem | null;
  loading: boolean;
  onOpen: (uuid: string) => void;
  onMove: (task: TaskBoardItem, status: BoardStatus) => void;
  touchTargetStatus: BoardStatus | null;
  onTouchDragStart: (task: TaskBoardItem) => void;
  onTouchDragMove: (clientX: number, clientY: number) => void;
  onTouchDragEnd: (clientX: number, clientY: number) => void;
  shouldSuppressClick: () => boolean;
}) {
  return (
    <div
      data-board-status={status}
      className={`flex h-full min-h-0 flex-col rounded-2xl border bg-white/70 p-3 shadow-sm transition-colors ${
        touchTargetStatus === status ? 'border-blue-400 bg-blue-50/70' : 'border-slate-200'
      }`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        const uuid = e.dataTransfer.getData('text/task-uuid');
        if (!uuid) return;
        const dragged = findTask(uuid);
        if (dragged) void onMove(dragged, status);
      }}
    >
      <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <div className="text-[13px] font-semibold text-slate-800">{title}</div>
          <div className="mt-0.5 text-[11px] text-slate-400">{tasks.length}</div>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(status)}`}>
          {status === 1 ? 'T' : status === 2 ? 'IP' : status === 3 ? 'D' : status === 5 ? 'C' : 'X'}
        </span>
      </div>

      {loading ? (
        <div className="space-y-2">
          <div className="h-20 rounded-xl bg-slate-100" />
          <div className="h-20 rounded-xl bg-slate-100" />
          <div className="h-20 rounded-xl bg-slate-100" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 pt-1">
          {tasks.map((task) => (
            <TaskCard
              key={task.uuid_id}
              task={task}
              onOpen={() => onOpen(task.uuid_id)}
              onTouchDragStart={onTouchDragStart}
              onTouchDragMove={onTouchDragMove}
              onTouchDragEnd={onTouchDragEnd}
              shouldSuppressClick={shouldSuppressClick}
            />
          ))}
          {tasks.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-3 text-xs text-slate-400">
              {title}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskCard({
  task,
  onOpen,
  onTouchDragStart,
  onTouchDragMove,
  onTouchDragEnd,
  shouldSuppressClick,
}: {
  task: TaskBoardItem;
  onOpen: () => void;
  onTouchDragStart: (task: TaskBoardItem) => void;
  onTouchDragMove: (clientX: number, clientY: number) => void;
  onTouchDragEnd: (clientX: number, clientY: number) => void;
  shouldSuppressClick: () => boolean;
}) {
  const { t } = useI18n();
  const dueDateInfo = getDueDateInfo(task.task_due_date);
  const isCompactStatus = task.task_status === 4 || task.task_status === 5;
  const categoryColors = getTaskCategoryColors(task.task_category_name);
  const assigneesLabel =
    task.assignees?.length
      ? task.assignees.slice(0, 2).map((a) => a.fullname).join(', ') + (task.assignees.length > 2 ? ` +${task.assignees.length - 2}` : '')
      : '—';

  return (
    <button
      type="button"
      onClick={() => {
        if (shouldSuppressClick()) return;
        onOpen();
      }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/task-uuid', task.uuid_id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onTouchStart={() => onTouchDragStart(task)}
      onTouchMove={(e) => {
        const touch = e.touches[0];
        if (!touch) return;
        e.preventDefault();
        onTouchDragMove(touch.clientX, touch.clientY);
      }}
      onTouchEnd={(e) => {
        const touch = e.changedTouches[0];
        if (!touch) return;
        onTouchDragEnd(touch.clientX, touch.clientY);
      }}
      onTouchCancel={() => onTouchDragEnd(-1, -1)}
      className={`relative w-full rounded-lg border text-left shadow-sm transition hover:z-10 hover:-translate-y-0.5 hover:shadow-md ${
        isCompactStatus ? 'p-2' : 'p-2.5'
      }`}
      style={{
        borderColor: categoryColors.border,
        backgroundColor: categoryColors.background,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          {!isCompactStatus && (
            <>
              <div className="text-xs font-medium uppercase tracking-[0.16em] text-blue-600">{task.power_plant_name}</div>
              <div className="mt-1 text-[10px] font-medium" style={{ color: categoryColors.text ?? '#7c3aed' }}>
                {task.task_category_name}
              </div>
            </>
          )}
          <h3 className={`${isCompactStatus ? 'mt-0 line-clamp-2 text-[11px]' : 'mt-1 text-xs'} line-clamp-2 font-semibold leading-4.5 text-slate-800`}>
            {task.title}
          </h3>
        </div>
        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${priorityTone(task.priority)}`}>
          {task.priority === 1
            ? t('tasks.priorityLabel.low')
            : task.priority === 2
              ? t('tasks.priorityLabel.middle')
              : t('tasks.priorityLabel.high')}
        </span>
      </div>

      {!isCompactStatus && (
        <>
          <div className="mt-1 line-clamp-2 text-[10px] text-slate-500">
            {task.description}
          </div>

          <div className="mt-2 text-[10px] text-slate-400">
            {t('tasks.titleSingle')} #{task.id}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
              Olusturma: {formatShortDate(task.task_created_date)}
            </span>
            {task.task_due_date && dueDateInfo && (
              <span className={`rounded-full px-2 py-0.5 ${dueDateInfo.className}`}>
                Termin: {dueDateInfo.label}
              </span>
            )}
          </div>

          <div className="mt-2.5 flex items-center justify-between gap-2 text-[10px] text-slate-500">
            <div className="inline-flex min-w-0 items-center gap-1.5">
              <CircleUserRound className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="truncate">{assigneesLabel}</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1.5">
                <MessageSquare className="h-2.5 w-2.5" />
                <span>{task.comment_count}</span>
              </span>
              {task.attachment_count > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Paperclip className="h-2.5 w-2.5" />
                  <span>{task.attachment_count}</span>
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </button>
  );
}

export function TaskBoardDrawer({
  uuid,
  onClose,
  onRefresh,
  onOpenCreate,
}: {
  uuid: string;
  onClose: () => void;
  onRefresh: () => void;
  onOpenCreate: (state: TaskCreateRouteState) => void;
}) {
  const { t } = useI18n();
  const [detail, setDetail] = useState<TaskBoardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'comments' | 'attachments' | 'relations' | 'history'>('comments');
  const [commentText, setCommentText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [lookups, setLookups] = useState<TaskCreateLookups | null>(null);
  const [assigneesOpen, setAssigneesOpen] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement | null>(null);
  const [titleValue, setTitleValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [dueDateValue, setDueDateValue] = useState('');
  const [saving, setSaving] = useState(false);

  const [relationQuery, setRelationQuery] = useState('');
  const [relationResults, setRelationResults] = useState<TaskBoardRelatedTask[]>([]);
  const [relationSearching, setRelationSearching] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  async function load(showLoading = true) {
    if (showLoading) setLoading(true);
    try {
      const [d, l] = await Promise.all([
        fetchTaskBoardDetail(uuid),
        fetchTaskCreateLookups().catch(() => null),
      ]);
      setDetail(d);
      setLookups(l);
      setTitleValue(d.title ?? '');
      setDescriptionValue(d.description ?? '');
      setAssigneeIds(d.assignees.map((assignee) => String(assignee.id)));
      setDueDateValue(d.task_due_date ?? '');
    } catch {
      toast.error(t('tasks.detailLoadError'));
      onClose();
    } finally {
      if (showLoading) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  useEffect(() => {
    if (!assigneesOpen) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (!assigneeDropdownRef.current?.contains(event.target as Node)) {
        setAssigneesOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setAssigneesOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [assigneesOpen]);

  useEffect(() => {
    if (!actionsOpen) return undefined;

    function handlePointerDown() {
      setActionsOpen(false);
    }

    document.addEventListener('click', handlePointerDown);
    return () => document.removeEventListener('click', handlePointerDown);
  }, [actionsOpen]);

  const imageAttachments = useMemo(
    () =>
      (detail?.attachments ?? [])
        .filter((a) => isImageAttachment(a.path_name, a.mime_type))
        .map((a) => ({ src: a.path_name, name: a.file_name || a.path_name.split('/').pop() || t('tasks.attachmentFallback') })),
    [detail, t],
  );

  const selectedAssigneeIds = useMemo(
    () => new Set(assigneeIds),
    [assigneeIds],
  );

  const selectedAssignees = useMemo(
    () => (lookups?.taskusers ?? []).filter((u) => selectedAssigneeIds.has(String(u.id))),
    [lookups?.taskusers, selectedAssigneeIds],
  );

  async function handleAddComment() {
    if (!detail) return;
    const text = commentText.trim();
    if (text.length < 1) return;
    try {
      const created = await addTaskComment(detail.uuid_id, text);
      setDetail((prev) => (prev ? { ...prev, comments: [created, ...prev.comments] } : prev));
      setCommentText('');
      onRefresh();
    } catch {
      toast.error(t('tasks.updateError'));
    }
  }

  async function handleUpload(file: File) {
    if (!detail) return;
    setUploading(true);
    try {
      const created = await addTaskAttachment(detail.uuid_id, file);
      setDetail((prev) => (prev ? { ...prev, attachments: [created, ...prev.attachments] } : prev));
      toast.success(t('tasks.attachmentAdded'));
      onRefresh();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg ?? t('tasks.updateError'));
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteAttachment(attachmentId: number) {
    if (!detail) return;
    try {
      await deleteTaskAttachment(detail.uuid_id, attachmentId);
      setDetail((prev) => (prev ? { ...prev, attachments: prev.attachments.filter((a) => a.id !== attachmentId) } : prev));
      toast.success(t('tasks.attachmentDeleted'));
      onRefresh();
    } catch {
      toast.error(t('tasks.updateError'));
    }
  }

  async function handleSearchRelations(text: string) {
    if (!detail) return;
    setRelationQuery(text);
    const trimmed = text.trim();
    if (trimmed.length < 2) {
      setRelationResults([]);
      return;
    }
    setRelationSearching(true);
    try {
      const res = await searchTasksApi(trimmed, detail.uuid_id);
      setRelationResults(res);
    } finally {
      setRelationSearching(false);
    }
  }

  async function handleAddRelation(item: TaskBoardRelatedTask) {
    if (!detail) return;
    try {
      await addTaskRelation(detail.uuid_id, item.uuid_id);
      toast.success(t('tasks.relationAdded'));
      await load();
      onRefresh();
    } catch {
      toast.error(t('tasks.updateError'));
    }
  }

  async function handleDeleteRelation(item: TaskBoardRelatedTask) {
    if (!detail) return;
    try {
      await deleteTaskRelation(detail.uuid_id, item.uuid_id);
      toast.success(t('tasks.relationDeleted'));
      await load();
      onRefresh();
    } catch {
      toast.error(t('tasks.updateError'));
    }
  }

  async function handleSave() {
    if (!detail || saving) return;

    setSaving(true);
    try {
      const nextDueDate = dueDateValue || null;
      await updateTask(detail.uuid_id, {
        title: titleValue.trim(),
        description: descriptionValue.trim(),
        dueDate: nextDueDate,
        assignees: assigneeIds.map(Number),
      });
      await load(false);
      onRefresh();
      toast.success(t('tasks.updatedSuccess'));
      onClose();
    } catch {
      toast.error(t('tasks.updateError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!detail) return;

    const confirmed = await confirmToast(t('agile.deleteConfirm'), t('agile.deleteConfirmMessage'));

    if (!confirmed) return;

    try {
      await deleteTask(detail.uuid_id);
      toast.success(t('agile.deleteSuccess'));
      onRefresh();
      onClose();
    } catch {
      toast.error(t('agile.deleteError'));
    }
  }

  function openCreateFlow(mode: 'duplicate' | 'related') {
    if (!detail) return;
    setActionsOpen(false);
    onOpenCreate({
      mode,
      sourceTaskUuidId: mode === 'related' ? detail.uuid_id : undefined,
      sourceTaskLabel: `#${detail.id}`,
      initialValues: {
        powerplantId: String(detail.power_plant_id),
        taskcategoryId: String(detail.task_category_id),
        taskassignees: detail.assignees.map((assignee) => String(assignee.id)),
        tasktitle: detail.title,
        taskdescription: detail.description,
        taskpriority: String(detail.priority),
        taskduedate: detail.task_due_date ?? '',
      },
    });
  }

  if (loading || !detail) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
        <div className="w-full max-w-6xl rounded-2xl bg-white p-6 shadow-2xl">
          <div className="text-sm text-slate-500">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  const dueDateDirty = (detail.task_due_date ?? '') !== dueDateValue;
  const titleDirty = detail.title !== titleValue;
  const descriptionDirty = detail.description !== descriptionValue;
  const currentAssigneeIds = detail.assignees.map((assignee) => String(assignee.id)).sort();
  const nextAssigneeIds = [...assigneeIds].sort();
  const assigneesDirty =
    currentAssigneeIds.length !== nextAssigneeIds.length ||
    currentAssigneeIds.some((id, index) => id !== nextAssigneeIds[index]);
  const isDirty = titleDirty || descriptionDirty || dueDateDirty || assigneesDirty;
  const canSave =
    !saving &&
    isDirty &&
    titleValue.trim().length >= 3 &&
    descriptionValue.trim().length >= 3 &&
    assigneeIds.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="flex h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t('tasks.titleSingle')}</div>
            <div className="mt-1 truncate pr-2 text-xl font-semibold text-slate-900">{titleValue || detail.title}</div>
            <div className="mt-1 truncate text-sm text-slate-500">
              #{detail.id} · {detail.power_plant_name} · {detail.task_category_name}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${priorityTone(detail.priority)}`}>
                P{detail.priority}
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                {t('tasks.due')}: {detail.task_due_date ? formatShortDate(detail.task_due_date) : '—'}
              </span>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center justify-end gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setActionsOpen((open) => !open);
                }}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-violet-200 bg-white text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50"
                aria-label={t('tasks.actions')}
              >
                <EllipsisVertical className="h-4 w-4" />
              </button>
              {actionsOpen && (
                <div
                  className="absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => openCreateFlow('duplicate')}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Copy className="h-4 w-4" />
                    {t('tasks.duplicate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => openCreateFlow('related')}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <GitBranch className="h-4 w-4" />
                    {t('tasks.createRelated')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      void handleDelete();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('agile.delete')}
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t('tasks.titleField')}
                  </div>
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    placeholder={t('tasks.titlePlaceholder')}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500"
                  />
                </div>

                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t('tasks.description')}
                  </div>
                  <textarea
                    value={descriptionValue}
                    onChange={(e) => setDescriptionValue(e.target.value)}
                    placeholder={t('tasks.descriptionPlaceholder')}
                    className="min-h-36 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                  />
              </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {t('tasks.assignees')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedAssignees.length ? (
                        selectedAssignees.map((assignee) => (
                          <span
                            key={assignee.id}
                            className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                          >
                            {assignee.fullname}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </div>

                    {lookups && (
                      <div className="mt-3" ref={assigneeDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setAssigneesOpen((v) => !v)}
                          className="inline-flex h-10 w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600"
                        >
                          <span className="truncate pr-2">
                            {selectedAssignees.length
                              ? selectedAssignees.map((u) => u.fullname).join(', ')
                              : t('tasks.select')}
                          </span>
                          <ChevronDown className={`h-4 w-4 text-slate-400 transition ${assigneesOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {assigneesOpen && (
                          <div className="relative">
                            <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                              <div className="max-h-64 overflow-y-auto p-2">
                                {lookups.taskusers.map((u) => {
                                  const checked = selectedAssigneeIds.has(String(u.id));
                                  return (
                                    <label
                                      key={u.id}
                                      className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          setAssigneeIds((current) => {
                                            if (checked) {
                                              return current.filter((id) => id !== String(u.id));
                                            }
                                            return [...current, String(u.id)];
                                          });
                                        }}
                                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                      />
                                      <span className="flex-1">{u.fullname}</span>
                                      {checked && <Check className="h-4 w-4 text-blue-600" />}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {t('tasks.due')}
                    </div>
                    <input
                      type="date"
                      value={dueDateValue}
                      onChange={(e) => setDueDateValue(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-[480px] flex-shrink-0 flex-col border-l border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
                {(
                  [
                    ['comments', t('agile.commentsShort'), MessageSquare, detail.comments?.length ?? 0],
                    ['attachments', t('agile.attachmentsShort'), Paperclip, detail.attachments?.length ?? 0],
                    ['relations', t('agile.relatedTasksShort'), GitBranch, detail.related_tasks?.length ?? 0],
                    ['history', t('agile.historyShort'), History, detail.history?.length ?? 0],
                  ] as const
                ).map(([key, label, Icon, count]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                      activeTab === key ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                    <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] ${activeTab === key ? 'bg-white/20' : 'bg-slate-200 text-slate-600'}`}>
                      {count}
                    </span>
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {activeTab === 'comments' && (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder={t('agile.commentPlaceholder')}
                        className="min-h-20 w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                      />
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddComment}
                          className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                        >
                          {t('agile.addComment')}
                        </button>
                      </div>
                    </div>

                    {(detail.comments ?? []).map((c) => (
                      <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <div className="inline-flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" />
                            <span className="font-semibold text-slate-700">{c.user_name ?? '—'}</span>
                          </div>
                          <div>{c.created_on}</div>
                        </div>
                        <div className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{c.comment_text}</div>
                      </div>
                    ))}

                    {!detail.comments?.length && (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-xs text-slate-500">
                        {t('agile.noComments')}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'attachments' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-3">
                      <div className="text-sm font-semibold text-slate-800">{t('tasks.attachments')}</div>
                      <div className="flex items-center gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            void handleUpload(file);
                            e.currentTarget.value = '';
                          }}
                        />
                        <button
                          type="button"
                          disabled={uploading}
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          <Upload className="h-4 w-4" />
                          {t('tasks.upload')}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {(detail.attachments ?? []).map((att) => {
                        const fileName = att.file_name || att.path_name.split('/').pop() || t('tasks.attachmentFallback');
                        const isImg = isImageAttachment(att.path_name, att.mime_type);
                        const imgIdx = isImg ? imageAttachments.findIndex((i) => i.src === att.path_name) : -1;
                        return (
                          <div key={att.id} className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
                            <a
                              href={att.path_name}
                              target="_blank"
                              rel="noreferrer"
                              className="min-w-0 flex-1"
                              onClick={(e) => {
                                if (!isImg) return;
                                e.preventDefault();
                                setCarouselIndex(imgIdx);
                              }}
                            >
                              <div className="truncate text-sm font-semibold text-blue-700">{fileName}</div>
                              <div className="mt-0.5 text-xs text-slate-500">{att.created_on}</div>
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteAttachment(att.id)}
                              className="rounded-xl border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                      {!detail.attachments?.length && (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-xs text-slate-500">
                          {t('agile.noAttachments')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'relations' && (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{t('agile.relatedTasks')}</div>
                      <input
                        value={relationQuery}
                        onChange={(e) => void handleSearchRelations(e.target.value)}
                        placeholder={t('agile.relatedTasksSearchPlaceholder')}
                        className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                      />
                      <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-slate-200">
                        {relationSearching ? (
                          <div className="px-3 py-3 text-xs text-slate-500">{t('common.loading')}</div>
                        ) : relationResults.length ? (
                          relationResults.map((item) => (
                            <button
                              key={item.uuid_id}
                              type="button"
                              onClick={() => void handleAddRelation(item)}
                              className="flex w-full items-start gap-2 border-b border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50 last:border-b-0"
                            >
                              <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${priorityTone(item.priority)}`}>
                                P{item.priority}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-slate-800">{item.title}</div>
                                <div className="truncate text-xs text-slate-500">
                                  #{item.id} · {item.power_plant_name} · {item.task_category_name}
                                </div>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-3 text-xs text-slate-500">{t('agile.relatedTasksSearchHint')}</div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {(detail.related_tasks ?? []).map((item) => (
                        <div key={item.uuid_id} className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3">
                          <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(item.task_status as BoardStatus)}`}>
                            {statusTitle(item.task_status as BoardStatus, t)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-slate-800">{item.title}</div>
                            <div className="truncate text-xs text-slate-500">
                              #{item.id} · {item.power_plant_name} · {item.task_category_name}
                            </div>
                            {item.description && (
                              <div className="mt-1 line-clamp-2 text-xs text-slate-500">{item.description}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleDeleteRelation(item)}
                            className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
                            title={t('common.delete')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {!detail.related_tasks?.length && (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-xs text-slate-500">
                          {t('agile.noRelatedTasks')}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <div className="space-y-2">
                    {(detail.history ?? []).map((h) => (
                      <div key={h.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <div className="font-semibold text-slate-700">{h.moved_by_name ?? '—'}</div>
                          <div>{h.created_on}</div>
                        </div>
                        <div className="mt-2 text-sm text-slate-800">
                          {h.event_type === 'status' && h.from_status && h.to_status ? (
                            <span>
                              {statusTitle(h.from_status as BoardStatus, t)} {'->'} {statusTitle(h.to_status as BoardStatus, t)}
                            </span>
                          ) : (
                            <span>{h.details ?? h.event_type}</span>
                          )}
                        </div>
                        {h.event_type === 'due_date' && (
                          <div className="mt-1 text-[10px] text-slate-400">
                            {t('tasks.due')} guncellendi
                          </div>
                        )}
                      </div>
                    ))}
                    {!detail.history?.length && (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-xs text-slate-500">
                        {t('agile.noHistory')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

        <div className="flex-shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex w-full items-center justify-end">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!canSave}
              className="inline-flex h-10 min-w-[132px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>

      {carouselIndex !== null && (
        <ImageCarouselModal
          images={imageAttachments}
          index={carouselIndex}
          onClose={() => setCarouselIndex(null)}
          onGoTo={setCarouselIndex}
        />
      )}
    </div>
  );
}
